// Stage 2 Step 2 — public card payment API client.
//
// HARD BOUNDARIES:
// - Only callable when CHECKOUT_STAGE2_API_ENABLED && CHECKOUT_PAYMENT_ENABLED.
//   The CheckoutFormStage2 component is responsible for those gates AND for
//   the runtime guards (buyer linked, accepted persisted quote, captured
//   acceptance, etc.).
// - NEVER calls /confirm. The customer card flow goes from an accepted
//   persisted quote + captured acceptance straight to
//   POST /api/checkout/{cs}/payment.
// - NEVER sends amounts, currency, buyer data, Offering/PBE IDs, Stripe
//   fields or a GUID. Only `expectedQuoteHash` (the accepted persisted
//   quoteHash) and `acceptancePublicId` (the captured acceptance id) are in
//   the body.
// - NEVER sends custom request headers. Only Accept + Content-Type.
// - Rejects GUID-shaped IDs and malformed hashes BEFORE fetch.
// - Response validation (currency / totals / Stripe URL hostname) is the
//   responsibility of `validatePaymentResponseForRedirect` below — callers
//   MUST NOT redirect without it.

import {
  CHECKOUT_STAGE2_API_BASE_URL,
  CheckoutStage2ApiError,
  isValidCheckoutSessionPublicId,
} from "./stage2Api";
import { appendCheckoutFlowVersion } from "./checkoutFlowVersion";
import { assertCheckoutMutationAllowed } from "./productionMode";

const GUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const HEX_RE = /^[0-9a-f]+$/i;
const MAX_HASH_LEN = 128;
const STRIPE_HOSTNAME = "checkout.stripe.com";
// ca_-prefixed plausible public id. Rejects GUID-shaped ids.
const ACCEPTANCE_PREFIX_RE = /^ca_[A-Za-z0-9_-]+$/;

// ---------------------------------------------------------------------------
// Request / response wire types
// ---------------------------------------------------------------------------

export interface StartCheckoutPaymentRequest {
  /** Accepted persisted quote's hash. Optimistic-concurrency guard. */
  expectedQuoteHash: string;
  /**
   * Captured acceptance public id ("ca_…"). REQUIRED by the backend when its
   * acceptance flag is on. Proves the buyer accepted the Terms + Refund policy
   * for this exact quote/context before payment can start.
   */
  acceptancePublicId: string;
}

/** Exact payment JSON serializer. The rollout marker is derived from the FE
 * flag at this boundary; callers cannot put it into identity/session state. */
export function serializeStartCheckoutPaymentBody(
  request: StartCheckoutPaymentRequest,
  neverBlockEnabled?: boolean,
): Record<string, unknown> {
  assertExpectedQuoteHash(request.expectedQuoteHash);
  assertAcceptancePublicId(request.acceptancePublicId);
  return appendCheckoutFlowVersion(
    {
      expectedQuoteHash: request.expectedQuoteHash.trim(),
      acceptancePublicId: request.acceptancePublicId.trim(),
    },
    neverBlockEnabled,
  );
}

export type PaymentProvider = "stripe_checkout" | (string & {});

export type PaymentStatus = "created" | "blocked" | "not_required" | (string & {});

export type PaymentNextAction =
  | "redirect_to_stripe"
  | "payment_not_required"
  | "payment_not_available"
  | "blocked"
  | (string & {});

export interface PaymentWarning {
  code: string;
  message: string;
  field?: string;
}

export interface PaymentTotals {
  net: string;
  tax: string;
  gross: string;
}

export interface StartCheckoutPaymentResponse {
  checkoutSessionPublicId: string;
  /**
   * Card-first responses are locked/accepted. Order-first idempotent lock keeps
   * the truthful order_created status after the Stripe Checkout Session exists.
   */
  quoteStatus: string;
  currency: string;
  totals: PaymentTotals;
  payment: {
    provider: PaymentProvider;
    status: PaymentStatus;
    checkoutUrl?: string;
  };
  warnings: PaymentWarning[];
  nextAction: PaymentNextAction;
  correlationId?: string;
}

/** A public-safe server response proving this request must not enter Stripe. */
export function isPaymentNotRequiredResponse(
  response: StartCheckoutPaymentResponse | null | undefined,
): boolean {
  return (
    response?.nextAction === "payment_not_required" &&
    response.payment?.status === "not_required"
  );
}

// ---------------------------------------------------------------------------
// Pre-flight validation
// ---------------------------------------------------------------------------

export function isValidExpectedQuoteHash(h: string | null | undefined): boolean {
  if (!h || typeof h !== "string") return false;
  const v = h.trim();
  if (!v) return false;
  if (v.length > MAX_HASH_LEN) return false;
  if (!HEX_RE.test(v)) return false;
  if (GUID_RE.test(v)) return false;
  return true;
}

function assertExpectedQuoteHash(h: string): void {
  if (!isValidExpectedQuoteHash(h)) {
    throw new Error("expectedQuoteHash: invalid (must be hex, ≤128 chars, non-GUID)");
  }
}

export function isValidAcceptancePublicId(id: string | null | undefined): boolean {
  if (!id || typeof id !== "string") return false;
  if (GUID_RE.test(id)) return false;
  return ACCEPTANCE_PREFIX_RE.test(id);
}

function assertAcceptancePublicId(id: string): void {
  if (!isValidAcceptancePublicId(id)) {
    throw new Error("acceptancePublicId: invalid (must be ca_-prefixed, non-GUID)");
  }
}

function assertCs(id: string): void {
  if (!isValidCheckoutSessionPublicId(id)) {
    throw new Error("checkoutSessionPublicId: invalid public id");
  }
}

interface ClientInit {
  fetchImpl?: typeof fetch;
  baseUrl?: string;
}

function pickCorrelationId(res: Response): string | null {
  return res.headers.get("x-correlation-id") || res.headers.get("x-request-id") || null;
}

async function readJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return text; }
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export async function startCheckoutPayment(
  checkoutSessionPublicId: string,
  request: StartCheckoutPaymentRequest,
  init?: ClientInit,
): Promise<StartCheckoutPaymentResponse> {
  assertCheckoutMutationAllowed("POST /api/checkout/{cs}/payment");
  assertCs(checkoutSessionPublicId);
  assertExpectedQuoteHash(request.expectedQuoteHash);
  assertAcceptancePublicId(request.acceptancePublicId);

  const base = (init?.baseUrl ?? CHECKOUT_STAGE2_API_BASE_URL).replace(/\/+$/, "");
  const fetchImpl = init?.fetchImpl ?? fetch;

  // EXACT body. Nothing else. No amounts, no buyer, no Offering/PBE. The
  // optional rollout marker comes only from the compiled frontend flag.
  const body = serializeStartCheckoutPaymentBody(request);

  const res = await fetchImpl(
    `${base}/api/checkout/${encodeURIComponent(checkoutSessionPublicId)}/payment`,
    {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  const parsed = await readJson(res);
  if (!res.ok) {
    throw new CheckoutStage2ApiError(
      res.status,
      parsed,
      `startCheckoutPayment failed: ${res.status}`,
      pickCorrelationId(res),
    );
  }
  return parsed as StartCheckoutPaymentResponse;
}

// ---------------------------------------------------------------------------
// Response validation (must pass before any redirect)
// ---------------------------------------------------------------------------

export interface AuthoritativeTotalsRef {
  currency: string;
  net: string;
  tax: string;
  gross: string;
}

export interface PaymentResponseValidation {
  ok: boolean;
  redirectUrl: string | null;
  reason: PaymentValidationReason | null;
}

export type PaymentValidationReason =
  | "session_id_mismatch"
  | "next_action_not_redirect"
  | "provider_not_stripe"
  | "status_not_created"
  | "checkout_url_missing"
  | "checkout_url_invalid"
  | "checkout_url_not_https"
  | "checkout_url_wrong_host"
  | "checkout_url_has_userinfo"
  | "checkout_url_wrong_port"
  | "currency_mismatch"
  | "totals_mismatch"
  | "quote_status_not_payable";

export type PaymentRedirectQuoteMode = "card_first" | "order_first";

// Default/card-first callers preserve the established locked|accepted contract.
// The durable-Order status is intentionally available only through the explicit
// order-first mode below.
const CARD_FIRST_PAYABLE_QUOTE_STATUSES_AFTER_PAYMENT = new Set([
  "locked",
  "accepted",
]);

export function validatePaymentResponseForRedirect(
  response: StartCheckoutPaymentResponse | null | undefined,
  requestedCheckoutSessionPublicId: string,
  authoritative: AuthoritativeTotalsRef,
  quoteMode: PaymentRedirectQuoteMode = "card_first",
): PaymentResponseValidation {
  const fail = (reason: PaymentValidationReason): PaymentResponseValidation => ({
    ok: false, redirectUrl: null, reason,
  });
  if (!response) return fail("session_id_mismatch");
  if (response.checkoutSessionPublicId !== requestedCheckoutSessionPublicId) {
    return fail("session_id_mismatch");
  }
  if (response.nextAction !== "redirect_to_stripe") return fail("next_action_not_redirect");
  if (response.payment?.provider !== "stripe_checkout") return fail("provider_not_stripe");
  if (response.payment?.status !== "created") return fail("status_not_created");
  const quoteStatusPayable =
    quoteMode === "order_first"
      ? response.quoteStatus === "order_created"
      : CARD_FIRST_PAYABLE_QUOTE_STATUSES_AFTER_PAYMENT.has(response.quoteStatus);
  if (!quoteStatusPayable) return fail("quote_status_not_payable");
  if ((response.currency || "").trim() !== (authoritative.currency || "").trim()) {
    return fail("currency_mismatch");
  }
  const t = response.totals;
  if (
    !t ||
    (t.net || "").trim() !== (authoritative.net || "").trim() ||
    (t.tax || "").trim() !== (authoritative.tax || "").trim() ||
    (t.gross || "").trim() !== (authoritative.gross || "").trim()
  ) {
    return fail("totals_mismatch");
  }
  const url = response.payment.checkoutUrl;
  if (!url) return fail("checkout_url_missing");
  let parsed: URL;
  try { parsed = new URL(url); } catch { return fail("checkout_url_invalid"); }
  if (parsed.protocol !== "https:") return fail("checkout_url_not_https");
  if (parsed.hostname.toLowerCase() !== STRIPE_HOSTNAME) return fail("checkout_url_wrong_host");
  // Reject deceptive authority: embedded userinfo (e.g.
  // https://evil@checkout.stripe.com/...) and any explicit non-default port
  // (e.g. https://checkout.stripe.com:444/...). The WHATWG URL parser
  // normalises the default HTTPS port (:443) to an empty `port`, so an
  // explicitly-specified 443 is still accepted here.
  if (parsed.username !== "" || parsed.password !== "") return fail("checkout_url_has_userinfo");
  if (parsed.port !== "") return fail("checkout_url_wrong_port");
  return { ok: true, redirectUrl: parsed.toString(), reason: null };
}

/**
 * Internal/technical warning codes that MUST never reach the customer UI.
 * They may be retained in dev-only diagnostics.
 */
export const SUPPRESSED_PAYMENT_WARNING_CODES: ReadonlySet<string> = new Set([
  "payment_attempt_not_durably_tracked",
]);

export function isCustomerSafePaymentWarning(code: string | null | undefined): boolean {
  if (!code) return false;
  return !SUPPRESSED_PAYMENT_WARNING_CODES.has(code);
}

// ---------------------------------------------------------------------------
// Payment-error classifier (pure)
// ---------------------------------------------------------------------------

export type PaymentErrorAction =
  | "payment_not_required" // exact complimentary graph — refresh status, never retry Stripe
  | "requote"             // stale-quote family: invalidate persisted readiness, fresh dry-run
  | "session_lost"        // checkout_session_not_found
  | "amount_invalid"      // quote_amount_invalid
  | "payment_unavailable" // backend blocked / capability off (no retry loop)
  | "transient"           // network or 5xx — keep accepted quote, allow deliberate retry
  | "unknown";

export interface PaymentErrorClassification {
  action: PaymentErrorAction;
  code: string | null;
  customerMessage: string;
}

const STALE_QUOTE_CODES = new Set([
  "quote_changed",
  "quote_expired",
  "quote_not_payable",
]);

export function readPaymentErrorCode(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const b = body as Record<string, unknown>;
  const candidates: unknown[] = [b.error, b.code, (b.body as any)?.error];
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return null;
}

/** Public-safe error discriminator for stale client payment attempts. */
export function isPaymentNotRequiredError(err: unknown): boolean {
  return err instanceof CheckoutStage2ApiError && readPaymentErrorCode(err.body) === "payment_not_required";
}

export function classifyPaymentError(err: unknown): PaymentErrorClassification {
  if (err instanceof CheckoutStage2ApiError) {
    const code = readPaymentErrorCode(err.body);
    if (code === "payment_not_required") {
      return {
        action: "payment_not_required",
        code,
        customerMessage: "This order is complimentary. Nothing to pay.",
      };
    }
    if (code && STALE_QUOTE_CODES.has(code)) {
      return {
        action: "requote",
        code,
        customerMessage:
          "Your total needs to be refreshed before payment. We're recalculating now.",
      };
    }
    if (code === "checkout_session_not_found") {
      return {
        action: "session_lost",
        code,
        customerMessage:
          "This checkout is no longer available. Please start again from the event page.",
      };
    }
    if (code === "quote_amount_invalid") {
      return {
        action: "amount_invalid",
        code,
        customerMessage:
          "Your total couldn't be confirmed. Please review your details or contact support.",
      };
    }
    if (code === "payment_disabled" || err.status === 503 || err.status === 409) {
      return {
        action: "payment_unavailable",
        code: code,
        customerMessage:
          "Online payment is temporarily unavailable. Please try again shortly.",
      };
    }
    if (err.status === 0 || err.status >= 500) {
      return {
        action: "transient",
        code,
        customerMessage:
          "Online payment is temporarily unavailable. Please try again shortly.",
      };
    }
    return {
      action: "unknown",
      code,
      customerMessage:
        "Online payment is temporarily unavailable. Please try again shortly.",
    };
  }
  return {
    action: "transient",
    code: null,
    customerMessage:
      "Online payment is temporarily unavailable. Please try again shortly.",
  };
}
